import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { TH_DAYS_SHORT, TH_MONTHS_SHORT, bkkTodayISO, fmtDate, fmtNumber } from './format.js'

/**
 * <DateRangePicker> — pick a date RANGE the way a flight is booked: click the
 * first day, click the last day, the band follows the mouse in between.
 *
 * Ported from statistic/statShared.jsx DateRange (the audit's best picker: one
 * fetch per pick, day count, max-length cap, no future days) with the kit's rules:
 *   - shows dd/mm/yyyy ค.ศ. (decision #1), never "7 ก.ย. 26"
 *   - "today" is the clinic's Bangkok day, never the browser's clock
 *   - the popover is portalled + fixed: never clipped by a Drawer or a filter card,
 *     flips above when there is no room below
 *   - two months on a wide screen, one on a phone
 *   - Esc closes the calendar only (not the Drawer behind it)
 *
 *   value        { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD' }  ('' + '' = all dates, with clearable)
 *   onChange     ({ start, end }) — fired ONCE per pick (after the second click / a preset)
 *   allowFuture  false (default) for history — reports, ledgers. true for appointments, schedules.
 *   maxDays      cap the span (0 = none); presets longer than it are not offered
 *   min          earliest selectable day ('YYYY-MM-DD')
 *   clearable    adds ทุกวันที่ (start = end = '')
 *   presets      override the quick buttons: keys of PRESETS below
 */

const pad = (n) => String(n).padStart(2, '0')
const toUTC = (iso) => { const [y, m, d] = iso.split('-').map(Number); return Date.UTC(y, m - 1, d) }
const isoAdd = (iso, n) => new Date(toUTC(iso) + n * 864e5).toISOString().slice(0, 10)
const spanDays = (a, b) => Math.round((toUTC(b) - toUTC(a)) / 864e5) + 1
const monthOf = (iso) => ({ year: Number(iso.slice(0, 4)), month: Number(iso.slice(5, 7)) })
const mIdx = (y, m) => y * 12 + (m - 1)
const fromIdx = (i) => ({ year: Math.floor(i / 12), month: (i % 12) + 1 })
const firstOf = (iso) => `${iso.slice(0, 8)}01`
const lastOf = (y, m) => `${y}-${pad(m)}-${pad(new Date(Date.UTC(y, m, 0)).getUTCDate())}`

// The payroll round holding `t` (gen2's window): the 26th of the previous month →
// the 25th of this one; from the 26th on, the round that has just opened. `delta`
// steps whole rounds (−1 = the round before).
function roundOf(t, delta) {
  const { year, month } = monthOf(t)
  const endIdx = mIdx(year, month) + (Number(t.slice(8)) >= 26 ? 1 : 0) + delta
  const s = fromIdx(endIdx - 1)
  const e = fromIdx(endIdx)
  return [`${s.year}-${pad(s.month)}-26`, `${e.year}-${pad(e.month)}-25`]
}

function monthCells(year, month) {
  const lead = new Date(Date.UTC(year, month - 1, 1)).getUTCDay()
  const days = new Date(Date.UTC(year, month, 0)).getUTCDate()
  const cells = new Array(lead).fill(null)
  for (let d = 1; d <= days; d++) cells.push(`${year}-${pad(month)}-${pad(d)}`)
  return cells
}

/** Quick ranges. Each returns [start, end] for a Bangkok `today`. */
export const PRESETS = {
  today: { label: 'วันนี้', range: (t) => [t, t] },
  last7: { label: '7 วัน', range: (t) => [isoAdd(t, -6), t] },
  last30: { label: '30 วัน', range: (t) => [isoAdd(t, -29), t] },
  last60: { label: '60 วัน', range: (t) => [isoAdd(t, -59), t] },
  thisMonth: { label: 'เดือนนี้', range: (t) => [firstOf(t), t] },
  lastMonth: {
    label: 'เดือนที่แล้ว',
    range: (t) => { const p = fromIdx(mIdx(...Object.values(monthOf(t))) - 1); return [`${p.year}-${pad(p.month)}-01`, lastOf(p.year, p.month)] },
  },
  next7: { label: '7 วันข้างหน้า', range: (t) => [t, isoAdd(t, 6)] },
  fullMonth: { label: 'ทั้งเดือนนี้', range: (t) => { const { year, month } = monthOf(t); return [firstOf(t), lastOf(year, month)] } },
  nextMonth: {
    label: 'เดือนหน้า',
    range: (t) => { const n = fromIdx(mIdx(...Object.values(monthOf(t))) + 1); return [`${n.year}-${pad(n.month)}-01`, lastOf(n.year, n.month)] },
  },
  // Payroll rounds (26 → 25) for HR pages. รอบปัจจุบัน usually ends in the future, so
  // a past-only picker leaves it out — pair it with allowFuture.
  thisRound: { label: 'รอบปัจจุบัน', range: (t) => roundOf(t, 0) },
  lastRound: { label: 'รอบที่แล้ว', range: (t) => roundOf(t, -1) },
}
const PAST_PRESETS = ['today', 'last7', 'last30', 'thisMonth', 'lastMonth']
const FUTURE_PRESETS = ['today', 'next7', 'fullMonth', 'nextMonth']

function MonthGrid({ year, month, lo, hi, today, isDisabled, onPick, onHover }) {
  const cells = useMemo(() => monthCells(year, month), [year, month])
  const single = lo && lo === hi
  return (
    <div className="w-[15.5rem]">
      <div className="text-center text-[13px] font-semibold text-slate-700 tabular-nums pb-1.5">{TH_MONTHS_SHORT[month - 1]} {year}</div>
      <div className="grid grid-cols-7 text-center">
        {TH_DAYS_SHORT.map((d) => <div key={d} className="text-[11px] text-slate-400 pb-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((iso, i) => {
          if (!iso) return <div key={`x${i}`} className="h-8" />
          const off = isDisabled(iso)
          const isLo = iso === lo
          const isHi = iso === hi
          const inside = !!lo && !!hi && iso > lo && iso < hi
          const band = !single && (inside || isLo || isHi) && lo && hi
          return (
            <div key={iso} className={`h-8 ${band ? 'bg-brand-50' : ''} ${band && isLo ? 'rounded-l-full' : ''} ${band && isHi ? 'rounded-r-full' : ''}`}>
              <button type="button" disabled={off} onClick={() => onPick(iso)} onMouseEnter={() => onHover(iso)}
                aria-label={fmtDate(iso)} aria-current={isLo || isHi ? 'date' : undefined}
                className={`w-full h-8 rounded-full text-[12.5px] tabular-nums transition ${
                  off ? 'text-slate-300 cursor-not-allowed'
                    : (isLo || isHi) ? 'bg-brand-600 text-white font-medium shadow-sm'
                      : inside ? 'text-brand-700 hover:bg-brand-100'
                        : `text-slate-700 hover:bg-slate-100 ${iso === today ? 'ring-1 ring-inset ring-slate-300' : ''}`}`}>
                {Number(iso.slice(8))}
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const POP_W_TWO = 590
const POP_W_ONE = 300
const POP_H = 370

export function DateRangePicker({
  value, onChange, allowFuture = false, maxDays = 0, min = '', clearable = false, presets,
  placeholder = 'ทุกวันที่', disabled = false, invalid = false, id, today: todayProp, className = '',
}) {
  const today = todayProp || bkkTodayISO()
  const start = value?.start || ''
  const end = value?.end || ''
  const hasRange = !!(start && end)

  const [open, setOpen] = useState(false)
  const [anchor, setAnchor] = useState(null)
  const [hover, setHover] = useState(null)
  const [cursor, setCursor] = useState(() => monthOf(start || today))
  const [pos, setPos] = useState(null)
  const btnRef = useRef(null)
  const popRef = useRef(null)

  // A saved range longer than the cap is clamped once (same as the original).
  useEffect(() => {
    if (!maxDays || !hasRange) return
    if (spanDays(start, end) > maxDays) onChange?.({ start, end: isoAdd(start, maxDays - 1) })
  }, [maxDays, start, end]) // eslint-disable-line react-hooks/exhaustive-deps

  const twoMonths = typeof window === 'undefined' || window.innerWidth >= 680

  const place = useCallback(() => {
    const el = btnRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const w = Math.min(twoMonths ? POP_W_TWO : POP_W_ONE, window.innerWidth - 16)
    const left = Math.max(8, Math.min(r.left, window.innerWidth - w - 8))
    const below = window.innerHeight - r.bottom
    const up = below < POP_H + 12 && r.top > below
    setPos(up ? { left, width: w, bottom: window.innerHeight - r.top + 4 } : { left, width: w, top: r.bottom + 4 })
  }, [twoMonths])

  useLayoutEffect(() => {
    if (!open) return undefined
    place()
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => { window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place) }
  }, [open, place])

  const close = () => { setOpen(false); setAnchor(null); setHover(null) }

  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => { if (!btnRef.current?.contains(e.target) && !popRef.current?.contains(e.target)) close() }
    // Capture phase + stopPropagation: Esc closes the calendar and never reaches
    // a Drawer / Modal's own Esc handler underneath.
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); close(); btnRef.current?.focus() } }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey, true)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey, true) }
  }, [open])

  const lo = anchor || start
  const hi = anchor ? (hover && hover > anchor ? hover : anchor) : end
  const capEnd = anchor && maxDays ? isoAdd(anchor, maxDays - 1) : ''
  // A day BEFORE the anchor is never blocked by the cap: clicking it restarts the pick.
  const isDisabled = (iso) => (!allowFuture && iso > today) || (min && iso < min) || (!!capEnd && iso > capEnd)

  const commit = (s, e) => { onChange?.({ start: s, end: e }); close() }
  const pick = (iso) => {
    if (isDisabled(iso)) return
    if (!anchor || iso < anchor) { setAnchor(iso); setHover(null); return }
    commit(anchor, iso)
  }

  const tIdx = mIdx(...Object.values(monthOf(today)))
  const leftIdx = mIdx(cursor.year, cursor.month)
  const shown = twoMonths ? 2 : 1
  const canNext = allowFuture || leftIdx + shown - 1 < tIdx
  const canPrev = !min || leftIdx > mIdx(...Object.values(monthOf(min)))
  const step = (d) => setCursor((c) => fromIdx(mIdx(c.year, c.month) + d))

  const openPicker = () => {
    if (disabled) return
    // Two months: show the END month on the right, so the current range is in view.
    const base = monthOf(end || start || today)
    setCursor(twoMonths && !allowFuture ? fromIdx(Math.max(mIdx(base.year, base.month) - 1, 0)) : base)
    setAnchor(null); setHover(null); setOpen(true)
  }

  const quick = (presets || (allowFuture ? FUTURE_PRESETS : PAST_PRESETS))
    .map((k) => ({ key: k, ...PRESETS[k] }))
    .filter((p) => p.range)
    .map((p) => ({ ...p, r: p.range(today) }))
    .filter((p) => (allowFuture || p.r[1] <= today) && (!maxDays || spanDays(p.r[0], p.r[1]) <= maxDays) && (!min || p.r[0] >= min))
    .filter((p, i, all) => all.findIndex((o) => o.r[0] === p.r[0] && o.r[1] === p.r[1]) === i)

  const label = !hasRange ? placeholder : start === end ? fmtDate(start) : `${fmtDate(start)} – ${fmtDate(end)}`
  const span = hasRange ? spanDays(start, end) : 0
  const ring = invalid
    ? 'border-rose-300 focus-visible:ring-4 focus-visible:ring-rose-100'
    : open ? 'border-brand-400 ring-4 ring-brand-100' : 'border-slate-200 hover:border-slate-300 focus-visible:border-brand-400 focus-visible:ring-4 focus-visible:ring-brand-100'

  return (
    <div className={`relative ${className}`}>
      <button ref={btnRef} id={id} type="button" disabled={disabled} onClick={() => (open ? close() : openPicker())}
        aria-haspopup="dialog" aria-expanded={open} aria-invalid={invalid || undefined}
        className={`w-full flex items-center gap-2 rounded-xl border bg-white px-3 py-2 text-[13.5px] text-left transition focus:outline-none disabled:bg-slate-50 disabled:text-slate-400 ${ring}`}>
        <CalendarDays size={15} className="text-slate-400 shrink-0" />
        <span className={`flex-1 truncate tabular-nums ${hasRange ? 'text-slate-800' : 'text-slate-400'}`}>{label}</span>
        {hasRange && <span className="text-[12px] text-slate-400 tabular-nums whitespace-nowrap">{fmtNumber(span)} วัน</span>}
        {clearable && hasRange && !disabled && (
          <span role="button" tabIndex={-1} title="ล้าง (ทุกวันที่)" aria-label="ล้าง"
            onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.stopPropagation(); onChange?.({ start: '', end: '' }) }}
            className="text-slate-300 hover:text-slate-600 -my-1 p-0.5 rounded"><X size={14} /></span>
        )}
        <ChevronDown size={15} className={`text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && pos && createPortal(
        <div ref={popRef} role="dialog" aria-label="เลือกช่วงวันที่"
          style={{ position: 'fixed', left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom }}
          className="z-[60] rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 p-3 animate-ss-pop-in">
          {(quick.length > 0 || clearable) && (
            <div className="flex flex-wrap items-center gap-1.5 pb-2.5">
              {quick.map((q) => (
                <button key={q.key} type="button" onClick={() => commit(q.r[0], q.r[1])}
                  className={`rounded-full border px-2.5 py-0.5 text-[12px] transition-colors ${
                    q.r[0] === start && q.r[1] === end ? 'border-brand-300 bg-brand-50 text-brand-700' : 'border-slate-200 bg-white text-slate-600 hover:border-brand-300 hover:text-brand-700'}`}>
                  {q.label}
                </button>
              ))}
              {clearable && (
                <button type="button" onClick={() => commit('', '')}
                  className={`rounded-full border px-2.5 py-0.5 text-[12px] ${!hasRange ? 'border-brand-300 bg-brand-50 text-brand-700' : 'border-slate-200 text-slate-600 hover:border-brand-300 hover:text-brand-700'}`}>
                  {placeholder}
                </button>
              )}
            </div>
          )}
          <div className="flex items-start justify-between gap-2">
            <button type="button" onClick={() => canPrev && step(-1)} disabled={!canPrev} aria-label="เดือนก่อนหน้า"
              className="p-1 rounded-lg text-slate-400 hover:text-brand-600 hover:bg-slate-50 disabled:opacity-30 shrink-0"><ChevronLeft size={16} /></button>
            <div className="flex gap-4" onMouseLeave={() => setHover(null)}>
              {Array.from({ length: shown }, (_, k) => fromIdx(leftIdx + k)).map((m) => (
                <MonthGrid key={`${m.year}-${m.month}`} {...m} lo={lo} hi={hi} today={today} isDisabled={isDisabled} onPick={pick} onHover={setHover} />
              ))}
            </div>
            <button type="button" onClick={() => canNext && step(1)} disabled={!canNext} aria-label="เดือนถัดไป"
              className="p-1 rounded-lg text-slate-400 hover:text-brand-600 hover:bg-slate-50 disabled:opacity-30 shrink-0"><ChevronRight size={16} /></button>
          </div>
          <div className="flex items-center justify-between gap-3 pt-2.5 mt-2.5 border-t border-slate-100 text-[12px] text-slate-500">
            <span>
              {anchor
                ? <>เริ่ม <b className="tabular-nums text-slate-700">{fmtDate(anchor)}</b> · เลือกวันสิ้นสุด{capEnd && <> (ได้ถึง <span className="tabular-nums">{fmtDate(capEnd)}</span>)</>}</>
                : hasRange
                  ? <>ช่วงที่เลือก <b className="tabular-nums text-slate-700">{fmtNumber(span)} วัน</b>{maxDays ? ` · สูงสุด ${maxDays} วัน` : ''} · คลิกวันเริ่มต้นเพื่อเลือกใหม่</>
                  : <>คลิกวันเริ่มต้น แล้วคลิกวันสิ้นสุด{maxDays ? ` · สูงสุด ${maxDays} วัน` : ''}</>}
            </span>
            <button type="button" onClick={close} className="text-slate-500 hover:text-slate-800 hover:underline shrink-0">ปิด</button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
